import React, { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import ButtonOutlined from "./StyledComponents/ButtonOutlined";
import UserContext from "../context/user-context";
import httpRequest from "../httpRequest";

// Importing Material-UI Icons
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';

const columns = [
  { id: "employee_code", label: "Employee Code", minWidth: 10 },
  { id: "employee_name", label: "Employee Name", minWidth: 10 },
  { id: "total_base_amount", label: "Expense Amount", minWidth: 10 },
  { id: "base_currency", label: "Base Currency", minWidth: 10 },
  { id: "company_name", label: "Company", minWidth: 10 },
  { id: "function_name", label: "Function", minWidth: 10 },
  { id: "status", label: "Status", minWidth: 10 },
];

// Mapping status to Material-UI icons
const statusIcons = {
  Pending: <PendingIcon style={{ marginRight: 8, color: '#e26300' }} />,
  Released: <CheckCircleIcon style={{ marginRight: 8, color: '#30914c' }} />,
  Approved: <CheckCircleIcon style={{ marginRight: 8, color: '#30914c' }} />,
};

const ExpenseInboxCard = () => {
  const [expensesData, setExpensesData] = useState([]);
  const userCtx = useContext(UserContext);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    const employeeCode = userCtx.employeeCode;
    setLoading(true);
    try {
      const [expensesResp] = await Promise.all([
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-expenses?exclude_fields=expenses&standing_employee_code=${employeeCode}&limit=5`),
      ]);
      setExpensesData(expensesResp.data.expenses);
    } catch (error) {
      setError("Error fetching data. Please try again later.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [userCtx]);

  const mappedExpenses = useMemo(() => {
    return expensesData.map((expense) => {
      const total_base_amount = parseFloat(expense.total_base_amount).toFixed(2);
      const status =
        expense.approval_flow.indexOf(userCtx.user_id) === expense.approval_flow.length - 1
          ? expense.status !== "Approved"
            ? "Released"
            : "Approved"
          : "Pending";

      return {
        ...expense,
        total_base_amount,
        status,
      };
    });
  }, [expensesData, userCtx]); // Adjust the dependencies as needed

  return (
    <Card
      header={<CardHeader avatar={<Icon name="my-view" />} subtitleText="Most Recent Expenses" titleText="My Inbox" />}
    >
      {loading ? (
        <div style={{ textAlign: "center" }}>Loading...</div>
      ) : error ? (
        <div style={{ color: "#e26300", textAlign: "center" }}>{error}</div>
      ) : (
        <>
          <TableContainer style={{ height: 370 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} sx={{ fontWeight: 'bold', fontFamily: 'SapFiori72' }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {mappedExpenses.map((row) => (
                  <TableRow hover key={row.id}>
                    {columns.map((column) => (
                      <TableCell key={column.id} sx={{ fontFamily: 'SapFiori72' }}>
                        {column.id === "status" ? (
                          <div style={{ display: "flex", alignItems: "center", color: getStatusColor(row[column.id]) }}>
                            {statusIcons[row[column.id]] || <ArrowCircleUpIcon style={{ marginRight: 8, color: '#758ca4' }} />} {/* Default icon if status not found */}
                            {row[column.id]}
                          </div>
                        ) : (
                          row[column.id]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Link to="/my-expenses" style={{ textDecoration: 'none' }}>
              <ButtonOutlined style={{ margin: "10px" }}>
                Show All
              </ButtonOutlined>
            </Link>
          </div>
        </>
      )}
    </Card>
  );
};

// Function to get the color based on status
const getStatusColor = (status) => {
  switch (status) {
    case "Pending":
      return '#e26300';
    case "Released":
    case "Approved":
      return '#30914c';
    default:
      return '#758ca4';
  }
};

export default ExpenseInboxCard;
